const REFRESH_STORAGE_KEY = "ziarcadia:autotranslator:last-refresh";
const REFRESH_RETRY_MS = 60_000;

type VersionGuardOptions = {
    buildId: string;
    log: (event: string, detail: Record<string, string | number>) => void;
};

// Only build metadata is persisted. Conversations stay in the current page.
export const createAutotranslatorVersionGuard = ({ buildId, log }: VersionGuardOptions) => {
    let latestBuildId = "";
    let pending: Promise<boolean> | null = null;
    let controller: AbortController | null = null;
    let destroyed = false;

    return {
        check(): Promise<boolean> {
            if (destroyed || !buildId || buildId === "local") return Promise.resolve(false);
            if (pending) return pending;
            controller = new AbortController();
            const signal = controller.signal;
            const timeout = window.setTimeout(() => controller?.abort(), 1800);
            pending = (async () => {
                try {
                    const response = await fetch(`/build-info.json?autotranslatorVersion=${Date.now()}`, {
                        cache: "no-store", credentials: "same-origin", signal
                    });
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data: unknown = await response.json();
                    const candidate = data && typeof data === "object" && "buildId" in data ? data.buildId : null;
                    if (typeof candidate !== "string" || !/^[A-Za-z0-9_.:-]{1,160}$/.test(candidate)) {
                        throw new Error("Invalid build metadata");
                    }
                    if (destroyed || signal.aborted) return false;
                    const changed = latestBuildId !== candidate && candidate !== buildId;
                    latestBuildId = candidate === buildId ? "" : candidate;
                    if (changed) log("version_update_detected", { runningBuild: buildId, latestBuild: latestBuildId });
                    return Boolean(latestBuildId);
                } catch (error) {
                    if (!destroyed) log("version_check_failed", { reason: error instanceof Error ? error.name : "unknown" });
                    return Boolean(latestBuildId) && !destroyed;
                } finally {
                    window.clearTimeout(timeout);
                    controller = null;
                    pending = null;
                }
            })();
            return pending;
        },
        refresh(): boolean {
            if (destroyed || !latestBuildId) return false;
            try {
                const marker = sessionStorage.getItem(REFRESH_STORAGE_KEY);
                let previous: { from?: string; to?: string; at?: number } | null = null;
                try { previous = JSON.parse(marker || "null"); } catch { /* Replace an invalid build-only marker. */ }
                // A stale CDN edge or failed script update must not trap the tab
                // in a reload loop. Do not persist any speech to implement this.
                if (previous?.from === buildId && previous?.to === latestBuildId &&
                    typeof previous.at === "number" && Number.isFinite(previous.at) &&
                    Date.now() >= previous.at && Date.now() - previous.at < REFRESH_RETRY_MS) {
                    log("version_refresh_deferred", { reason: "recent_attempt", latestBuild: latestBuildId });
                    return false;
                }
                sessionStorage.setItem(REFRESH_STORAGE_KEY, JSON.stringify({ from: buildId, to: latestBuildId, at: Date.now() }));
            } catch {
                log("version_refresh_deferred", { reason: "storage_unavailable", latestBuild: latestBuildId });
                return false;
            }
            log("version_refresh", { runningBuild: buildId, latestBuild: latestBuildId });
            window.location.reload();
            return true;
        },
        destroy() {
            destroyed = true;
            controller?.abort();
        }
    };
};
